import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { Button } from './ui/button';
import { useForceLightThemePage } from '../hooks/useForceLightThemePage';

type WritingType = 'Novel' | 'Journalism' | 'Essays' | 'Academic' | 'Other';
type WritingTool = 'Word' | 'Google Docs' | 'Scrivener' | 'ProWritingAid' | 'Overleaf' | 'Other';
type CollaborationType = 'Solo' | 'Small team' | 'Editorial team';

export const WaitingList: React.FC = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [writingTypes, setWritingTypes] = useState<WritingType[]>([]);
  const [writingTools, setWritingTools] = useState<WritingTool[]>([]);
  const [collaborationType, setCollaborationType] = useState<CollaborationType | ''>('');
  const [additionalInfo, setAdditionalInfo] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
  const [referral, setReferral] = useState<string>('');
  const [referralDetail, setReferralDetail] = useState<string>('');

  useForceLightThemePage();

  const handleWritingTypeChange = (type: WritingType): void => {
    setWritingTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleWritingToolChange = (tool: WritingTool): void => {
    setWritingTools((prev) =>
      prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]
    );
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!acceptedTerms) {
      setError(t('must_accept_terms'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/waiting-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          writingTypes,
          writingTools,
          collaborationType: collaborationType || undefined,
          additionalInfo: additionalInfo.trim() || undefined,
          referral: referral || undefined,
          referralDetail: referralDetail.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit request');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
      setIsSubmitting(false);
    }
  };

  const writingTypeOptions: WritingType[] = ['Novel', 'Journalism', 'Essays', 'Academic', 'Other'];
  const writingToolOptions: WritingTool[] = ['Word', 'Google Docs', 'Scrivener', 'ProWritingAid', 'Overleaf', 'Other'];
  const collaborationTypeOptions: CollaborationType[] = ['Solo', 'Small team', 'Editorial team'];

  const getWritingTypeLabel = (type: WritingType): string => {
    const key = `waiting_list_writing_type_${type.toLowerCase()}`;
    return t(key);
  };

  const getCollaborationTypeLabel = (type: CollaborationType): string => {
    const key = `waiting_list_collaboration_type_${type.toLowerCase().replace(/\s+/g, '_')}`;
    return t(key);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{
        maxWidth: '700px',
        width: '100%',
        padding: '2rem',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}>
        <h1 style={{ marginBottom: '1rem', fontSize: '2rem', fontWeight: '700', textAlign: 'center' }}>
          {t('waiting_list_header')}
        </h1>
        <p style={{ marginBottom: '2rem', fontSize: '1rem', color: '#6b7280', textAlign: 'center', lineHeight: '1.6' }}>
          {t('waiting_list_subheadline')}
        </p>
        {submitted ? (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.5rem', fontWeight: '600', color: '#374151' }}>
              {t('waiting_list_title')}
            </h2>
            <div style={{ marginBottom: '1.5rem', color: '#374151', lineHeight: '1.6', textAlign: 'left' }}>
              <p style={{ marginBottom: '1rem', whiteSpace: 'pre-line' }}>
                {t('waiting_list_body')}
              </p>
              <p style={{ marginBottom: '1rem' }}>
                {t('waiting_list_email_sent')}
              </p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {t('waiting_list_reply_hint')}
              </p>
            </div>
            <Button onClick={() => navigate('/')}>
              {t('back_to_scribane')}
            </Button>
          </div>
        ) : (
          <>
            <ul style={{
              marginBottom: '2rem',
              paddingLeft: '1.5rem',
              listStyle: 'disc',
              color: '#374151',
            }}>
              <li style={{ marginBottom: '0.5rem' }} dangerouslySetInnerHTML={{ __html: t('waiting_list_bullet_1') }} />
              <li style={{ marginBottom: '0.5rem' }} dangerouslySetInnerHTML={{ __html: t('waiting_list_bullet_2') }} />
              <li style={{ marginBottom: '0.5rem' }} dangerouslySetInnerHTML={{ __html: t('waiting_list_bullet_3') }} />
              <li style={{ marginBottom: '0.5rem' }} dangerouslySetInnerHTML={{ __html: t('waiting_list_bullet_4') }} />
              <li style={{ marginBottom: '0.5rem' }} dangerouslySetInnerHTML={{ __html: t('waiting_list_bullet_5') }} />
              <li dangerouslySetInnerHTML={{ __html: t('waiting_list_bullet_6') }} />
            </ul>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor="waiting-list-email"
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                  }}
                >
                  {t('email')}
                </label>
                <input
                  id="waiting-list-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.75rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                  }}
                >
                  {t('waiting_list_what_do_you_write')}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {writingTypeOptions.map((type) => (
                    <label
                      key={type}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={writingTypes.includes(type)}
                        onChange={() => handleWritingTypeChange(type)}
                        disabled={isSubmitting}
                        style={{
                          width: '1rem',
                          height: '1rem',
                          cursor: 'pointer',
                        }}
                      />
                      <span>{getWritingTypeLabel(type)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.75rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                  }}
                >
                  {t('waiting_list_writing_tools')}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {writingToolOptions.map((tool) => (
                    <label
                      key={tool}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={writingTools.includes(tool)}
                        onChange={() => handleWritingToolChange(tool)}
                        disabled={isSubmitting}
                        style={{
                          width: '1rem',
                          height: '1rem',
                          cursor: 'pointer',
                        }}
                      />
                      <span>{tool}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.75rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                  }}
                >
                  {t('waiting_list_collaboration')}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {collaborationTypeOptions.map((type) => (
                    <label
                      key={type}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      <input
                        type="radio"
                        name="collaboration-type"
                        checked={collaborationType === type}
                        onChange={() => setCollaborationType(type)}
                        disabled={isSubmitting}
                        style={{
                          width: '1rem',
                          height: '1rem',
                          cursor: 'pointer',
                        }}
                      />
                      <span>{getCollaborationTypeLabel(type)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor="waiting-list-additional-info"
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                  }}
                >
                  {t('waiting_list_additional_info')}
                </label>
                <textarea
                  id="waiting-list-additional-info"
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  disabled={isSubmitting}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {error && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#991b1b',
                  fontSize: '0.875rem',
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.75rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                  }}
                >
                  {t('waiting_list_referral')}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {['Friend/family', 'Google', 'Podcast', 'Twitter', 'Instagram', 'TikTok', 'Other'].map((option) => (
                    <label
                      key={option}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      <input
                        type="radio"
                        name="referral"
                        value={option}
                        checked={referral === option}
                        onChange={(e) => {
                          setReferral(e.target.value);
                          if (e.target.value !== 'Friend/family' && e.target.value !== 'Other') {
                            setReferralDetail('');
                          }
                        }}
                        disabled={isSubmitting}
                        style={{
                          width: '1rem',
                          height: '1rem',
                          cursor: 'pointer',
                        }}
                      />
                      <span>{t(`waiting_list_referral_${option.toLowerCase().replace(/[\/\s]/g, '_')}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {(referral === 'Friend/family' || referral === 'Other') && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label
                    htmlFor="waiting-list-referral-detail"
                    style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151',
                    }}
                  >
                    {referral === 'Friend/family' ? t('waiting_list_referral_detail_friend') : t('waiting_list_referral_detail_other')}
                  </label>
                  <input
                    id="waiting-list-referral-detail"
                    type="text"
                    value={referralDetail}
                    onChange={(e) => setReferralDetail(e.target.value)}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: '#374151',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    disabled={isSubmitting}
                    required
                    style={{
                      marginTop: '0.125rem',
                      width: '1rem',
                      height: '1rem',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      flexShrink: 0,
                    }}
                  />
                  <span>
                    {t('waiting_list_accepts_terms')}{' '}
                    <Link
                      to="/terms"
                      style={{
                        color: 'var(--scribane-black)',
                        textDecoration: 'underline',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('terms_of_service_link')}
                    </Link>
                  </span>
                </label>
              </div>
              <Button
                type="submit"
                disabled={isSubmitting || !acceptedTerms}
                style={{ width: '100%', marginBottom: '0.75rem' }}
              >
                {isSubmitting ? t('submitting') : t('waiting_list_button')}
              </Button>
              <p style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                textAlign: 'center',
                marginBottom: '1rem',
              }}>
                {t('waiting_list_microcopy')}
              </p>
            </form>
            <div style={{ textAlign: 'center' }}>
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                style={{ fontSize: '0.875rem' }}
              >
                {t('back_to_home')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

